import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlgoliaFilters } from '../../algolia/util/algolia-util';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import FreeCsvDownloadDialog from '../csv-dialog/FreeCsvDownloadDialog';
import moment from 'moment-timezone';
import { getOrganizationsByIds } from '../../../utils/bmResidenceService';
import Redux from '../../../redux/ReduxConnector';
import { SumamoruPlan } from '../../../enums/common/sumamoru-plan-types';
import { SumamoruProjectType } from '../../../enums/common/sumamoru-project-type';
import { CsvDownloadType, registerDownloadLog } from '../../../utils/csvDownloadLogService';
import { setAdditonalService } from '../../../enums/common/sumamoru-additional-services';

interface P {
  index: any;
  onClose: () => any;
  onComplete: () => any;
}

/**
 * スマモル賃貸サービスの種類です。
 *
 * OG域内: 大阪ガス様の管理内のうち関西圏内に適用可能なプラン
 * OG域内 電気のみ: 大阪ガス様の管理内のうち関西圏外に適用可能なプランで、電気のみのプラン
 * OG域外: 大阪ガス様の管理内のうち関西圏外(中部九州)に適用可能なプラン
 * CDE: CDE様の管理内に適用可能なプラン
 * CDE 電気のみ: CDE様の管理内に適用可能なプランで、電気のみのプラン
 * スマモルでない通常物件: スマモル組織においてスマモル賃貸サービスを適用しないプラン
 *
 * 以下は2024/04時点で運用されていないプラン
 * 京葉ガス: 京葉ガス様の管理内に適用可能なプラン
 * 西部ガス: 西部ガス様の管理内に適用可能なプラン
 *
 */

const FilterSelectedArea = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  box-sizing: border-box;
`;

export enum SumamoruApplicationStatus {
  application_request = 'application_request', // 申請依頼
  application_request_canceled = 'application_request_canceled', // 申請依頼キャンセル済み
  applied = 'applied', // 申請受付(申込み済)
  applied_canceled = 'applied_canceled' // 申請受付後の取消
}

export enum SumamoruServiceStatus {
  all_in_use = 'all_in_use', // 全サービス利用中
  only_electrical_canceled = 'only_electrical_canceled', // 電気サービスのみ解約
  all_canceled = 'all_canceled' // 全サービス解約済
}

export enum SpaceStatus {
  vacant = 'vacant', // 空室(公開中)
  applied = 'applied', // 申し込み有
  occupyScheduled = 'occupyScheduled', // 入居予定
  occupied = 'occupied', // 入居中
  leaveScheduled = 'leaveScheduled', // 退去予定
  leaveProcessing = 'leaveProcessing', // 退去処理中
  closed = 'closed', // 停止中
  none = 'none'
}

// SpaceStatusとDictKeyのマッピング定義
// Enum値が追加された際に、本ファイルの修正漏れを型チェックを利用して防ぐために切り出し
const SpaceStatusDictValueDefinition: { [key in SpaceStatus]: string } = {
  [SpaceStatus.vacant]: '公開中',
  [SpaceStatus.applied]: '申込有',
  [SpaceStatus.occupyScheduled]: '入居予定',
  [SpaceStatus.occupied]: '入居中',
  [SpaceStatus.leaveScheduled]: '退去予定',
  [SpaceStatus.leaveProcessing]: '退去処理中',
  [SpaceStatus.closed]: '停止中',
  [SpaceStatus.none]: 'ステータスなし'
};

const SumamoruApplicationStatusDictValueDefinition: {
  [key in SumamoruApplicationStatus]: string;
} = {
  [SumamoruApplicationStatus.application_request]: 'URL発行済',
  [SumamoruApplicationStatus.application_request_canceled]: 'URL発行後取消',
  [SumamoruApplicationStatus.applied]: '申込完了',
  [SumamoruApplicationStatus.applied_canceled]: '申込完了後取消'
};

const SumamoruServiceStatusDictValueDefinition: {
  [key in SumamoruServiceStatus]: string;
} = {
  [SumamoruServiceStatus.all_in_use]: '全サービス利用中',
  [SumamoruServiceStatus.only_electrical_canceled]: '電気のみ解約',
  [SumamoruServiceStatus.all_canceled]: '全サービス解約済'
};

const NO_RESULT_CODE = '---';

const PropertiesCsvDownloadDialog: React.FC<P> = props => {
  const { onClose, onComplete, index } = props;
  // const dict = useDictionary(dictDef);

  const onDownloadComplete = useCallback(async () => {
    // ログ記録API
    const res = await registerDownloadLog(CsvDownloadType.ALL_PROPERTY);
    if (!res) console.log('error: 全件ダウンロード: ログ記録失敗しました。');
    onComplete();
  }, [onComplete]);
  const [nbHits, setNbHits] = useState<number>(0);
  const [csvRows, setCsvRows] = useState<any[]>([]);

  const csvHeaders = useMemo(
    () => [
      {
        key: 'companyName',
        label: '管理会社名'
      },
      {
        key: 'sumamoruCode',
        label: '建物No'
      },
      {
        key: 'postcode',
        label: '郵便番号'
      },
      {
        key: 'address',
        label: '住所'
      },
      {
        key: 'buildingName',
        label: '建物名'
      },
      {
        key: 'propertyName',
        label: '物件名'
      },
      {
        key: 'status',
        label: '物件ステータス'
      },
      {
        key: 'deviceCount',
        label: 'デバイス設置数'
      },
      {
        key: 'occupyScheduledDate',
        label: '入居予定日'
      },
      {
        key: 'sumamoruApplicationStatus',
        label: 'スマモル申込状況'
      },
      {
        key: 'sumamoruElectricalStatus',
        label: 'スマモル申込内容'
      },
      {
        key: 'sumamoruServiceStatus',
        label: 'スマモル利用状況'
      },
      {
        key: 'sumamoruAdditionalService1',
        label: '付帯サービスID1'
      },
      {
        key: 'sumamoruAdditionalService2',
        label: '付帯サービスID2'
      }
    ],
    []
  );

  const fileName = useMemo(() => `sumamoryProperties.csv`, []);

  let getDownloadData: () => void;
  getDownloadData = useCallback(async () => {
    const filters: AlgoliaFilters = {
      'sumamoruData.sumamoruLinkable': ['true'],
      attributes: ['property'],
    };
    const excludeFilters: AlgoliaFilters = {
      'sumamoruData.sumamoruProjectTypes': [
        SumamoruProjectType.OGExternal,
        SumamoruProjectType.OGInternal,
        SumamoruProjectType.OGInternalOnlyElectricity,
        SumamoruProjectType.KeiyouGas,
        SumamoruProjectType.SaibuGas,
        SumamoruProjectType.NotSumamoru,
      ],
    };

    const allHits = await index.getAllHitsByBrowseResponse({
      searchWord: '',
      filters: filters,
      excludeFilters: excludeFilters,
      facets: {},
      offset: 0
    });

    const hits = allHits as any[];
    const uniqOrganizationIds: Array<string> = Array.from(
      new Set(hits.map(v => v.organizationId))
    ).filter(v => !!v);
    const uniqOrganizationsRes = await getOrganizationsByIds(
      uniqOrganizationIds,
      Redux.getStore().user.accessToken
    );
    const uniqOrganizations = uniqOrganizationsRes.data
      ? uniqOrganizationsRes.data
      : [];

    const convertedRows = hits.map(res => {
      const companyName = (
        uniqOrganizations.find(v => v.id === res.organizationId) || {
          name: NO_RESULT_CODE
        }
      ).name;
      const propertySumamoru = (res.addOns && res.addOns.sumamoruData) || {};
      const contractSumamoru =
        (res.sumamoruData &&
          res.sumamoruData.contracts &&
          res.sumamoruData.contracts.find(contract => !!contract.isCurrent)) ||
        {};

      // 建物での選択可能プラン (DB的には複数入るが座組的には1つなのでこれだけ出す予定)
      const sumamoruAdditionalServices = propertySumamoru.sumamoruAdditionalServices || [];

      const sumamoruPlan =
        Array.isArray(propertySumamoru.sumamoruPlans) &&
        propertySumamoru.sumamoruPlans.length > 0
          ? propertySumamoru.sumamoruPlans.join('|')
          : '';

      // 付帯サービス
      const [sumamoruAdditionalService1 = '', sumamoruAdditionalService2 = ''] = 
        sumamoruAdditionalServices.length > 0 ?
        propertySumamoru.sumamoruAdditionalServices : setAdditonalService(sumamoruPlan);

      return {
        companyName,
        sumamoruCode: propertySumamoru.sumamoruCode || NO_RESULT_CODE,
        postcode: res.postcode || NO_RESULT_CODE,
        address: res.address || NO_RESULT_CODE,
        buildingName:
          res.parentNames && res.parentNames[0]
            ? res.parentNames[res.parentNames.length - 1]
            : NO_RESULT_CODE,
        propertyName: res.name,
        status:
          !!res.status && res.status !== 'none'
            ? SpaceStatusDictValueDefinition[res.status]
            : NO_RESULT_CODE,
        occupyScheduledDate: contractSumamoru.occupyScheduledDate
          ? moment
              .tz(contractSumamoru.occupyScheduledDate, 'Asia/Tokyo')
              .format('YYYY/MM/DD')
          : NO_RESULT_CODE,
        sumamoruApplicationStatus: contractSumamoru.applicationStatus
          ? SumamoruApplicationStatusDictValueDefinition[
              contractSumamoru.applicationStatus
            ]
          : 'URL未発行',
        sumamoruServiceStatus:
          contractSumamoru.sumamoruStatus &&
          contractSumamoru.applicationStatus ===
            SumamoruApplicationStatus.applied
            ? SumamoruServiceStatusDictValueDefinition[
                contractSumamoru.sumamoruStatus
              ]
            : '未申込',
        deviceCount: Array.isArray(res.bitlockIds) ? res.bitlockIds.length : 0,
        sumamoruElectricalStatus:
          contractSumamoru.hasElectricContract === undefined
            ? NO_RESULT_CODE
            : contractSumamoru.hasElectricContract
            ? '電気あり'
            : 'サービスのみ',
        sumamoruAdditionalService1: sumamoruAdditionalService1,
        sumamoruAdditionalService2: sumamoruAdditionalService2
      };
    });
    setCsvRows(convertedRows);
    setNbHits(allHits.length);
    return;
  }, [index]);

  const confirmButton = useMemo(
    () => ({
      text: 'ダウンロード',
      onClick: onDownloadComplete,
      disabled: !csvRows || (csvRows && csvRows.length <= 0)
    }),
    [csvRows, onDownloadComplete]
  );

  const cancelButton = useMemo(
    () => ({
      text: 'キャンセル',
      onClick: onClose,
      disabled: false
    }),
    [onClose]
  );

  useEffect(() => {
    getDownloadData();
  }, [getDownloadData]);

  return (
    <FreeCsvDownloadDialog
      title={'スマモル物件全件ダウンロード'}
      confirmButton={confirmButton}
      cancelButton={cancelButton}
      processing={false}
      titleColor={true}
      csvHeaders={csvHeaders}
      csvRows={csvRows}
      fileName={fileName || '無題'}
      onClickDownload={onDownloadComplete}
    >
      <Typography
        style={{
          boxSizing: 'border-box',
          padding: 8,
          borderBottom: '1px solid #E0E0E0'
        }}
      >
        件数 : {nbHits}件
      </Typography>
      <FilterSelectedArea></FilterSelectedArea>
    </FreeCsvDownloadDialog>
  );
};

export default PropertiesCsvDownloadDialog;
